import { create } from 'zustand';
import { StoreState, Message, Config } from '@/types';
import { api } from '@/lib/api';

export const useChatStore = create<StoreState>((set, get) => ({
  sessions: {},
  currentSessionId: null,
  isLoading: false,
  error: null,
  config: {
    llm: {
      provider: 'openai',
      model: 'gpt-3.5-turbo',
      temperature: 0.7,
      maxTokens: 2000,
    },
    tools: {
      enabled: true,
      mcpServers: [],
    },
  },

  createSession: (sessionId: string) => {
    set((state) => ({
      sessions: {
        ...state.sessions,
        [sessionId]: {
          id: sessionId,
          name: `Session ${Object.keys(state.sessions).length + 1}`,
          messages: [],
          createdAt: Date.now(),
          updatedAt: Date.now(),
        },
      },
      currentSessionId: sessionId,
    }));
  },

  switchSession: (sessionId: string) => {
    set({ currentSessionId: sessionId });
  },

  deleteSession: (sessionId: string) => {
    set((state) => {
      const newSessions = { ...state.sessions };
      delete newSessions[sessionId];
      return {
        sessions: newSessions,
        currentSessionId:
          state.currentSessionId === sessionId
            ? null
            : state.currentSessionId,
      };
    });
  },

  sendMessage: async (sessionId: string, message: string) => {
    const state = get();
    
    // Check if session exists, if not create it
    if (!state.sessions[sessionId]) {
      state.createSession(sessionId);
    }

    // Add user message
    const userMessage: Message = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: message,
      timestamp: Date.now(),
    };
    
    state.addMessage(sessionId, userMessage);
    state.setLoading(true);
    state.clearError();

    try {
      // Send message to API
      const response = await api.chat(sessionId, message);

      // Add assistant response
      const assistantMessage: Message = {
        id: `msg-${Date.now() + 1}`,
        role: 'assistant',
        content: response,
        timestamp: Date.now(),
      };
      
      state.addMessage(sessionId, assistantMessage);
    } catch (error: any) {
      state.setError(error.message || 'Failed to send message');
    } finally {
      state.setLoading(false);
    }
  },

  addMessage: (sessionId: string, message: Message) => {
    set((state) => {
      if (!state.sessions[sessionId]) {
        return state;
      }

      return {
        sessions: {
          ...state.sessions,
          [sessionId]: {
            ...state.sessions[sessionId],
            messages: [...state.sessions[sessionId].messages, message],
            updatedAt: Date.now(),
          },
        },
      };
    });
  },

  setLoading: (loading: boolean) => {
    set({ isLoading: loading });
  },

  setError: (error: string | null) => {
    set({ error });
  },

  clearError: () => {
    set({ error: null });
  },

  setConfig: (config: Config) => {
    set({ config });
  },
}));
